import { isEqual } from 'lodash';
import {
  getHotelCancellationFee,
  getHotelValidate,
} from '@shared/services/services';

const getPriceObject = valuation =>
  valuation?.rate?.[0]?.rates?.[0]?.price ?? null;

const normalizeStringArray = arr => {
  if (!arr) return [];

  const normalized = Array.isArray(arr) ? arr : [arr];

  const cleaned = normalized.map(s =>
    typeof s === 'string' ? s.trim().replace(/\s+/g, ' ') : String(s)
  );

  return [...new Set(cleaned)];
};

export const findBookingValuationChanges = (
  previousValuation,
  newValuation,
  priceChanged = false
) => {
  if (!newValuation) return null;

  const changes = {};

  // 💰 PRICE (source of truth: backend)
  if (priceChanged && previousValuation) {
    changes.rate = {
      removed: getPriceObject(previousValuation),
      added: getPriceObject(newValuation),
    };
  }

  // 📜 CANCELLATION POLICIES
  if (previousValuation?.cancellation_policies?.length > 0) {
    const prevPolicies = normalizeStringArray(
      previousValuation?.cancellation_policies
    );
    const nextPolicies = normalizeStringArray(
      newValuation?.cancellation_policies
    );

    if (!isEqual(prevPolicies, nextPolicies)) {
      changes.cancellation_policies = {
        removed: prevPolicies,
        added: nextPolicies,
      };
    }
  }

  // 🚫 CAN BE CANCELED & ⏰ DEADLINE CANCEL
  if (previousValuation?.CanBeCanceled !== undefined) {
    const prevCanCancel = previousValuation.CanBeCanceled;
    const nextCanCancel = newValuation?.CanBeCanceled;

    const prevDeadline = previousValuation.DeadLineCancel;
    const nextDeadline = newValuation?.DeadLineCancel;

    const deadlineChanges = {};

    if (
      typeof prevCanCancel === 'boolean' &&
      typeof nextCanCancel === 'boolean' &&
      prevCanCancel !== nextCanCancel
    ) {
      deadlineChanges.CanBeCanceled = {
        removed: prevCanCancel,
        added: nextCanCancel,
      };
    }

    const shouldCompareDeadline =
      prevCanCancel !== false || nextCanCancel !== false;

    if (
      shouldCompareDeadline &&
      typeof prevDeadline === 'number' &&
      typeof nextDeadline === 'number' &&
      prevDeadline !== nextDeadline
    ) {
      deadlineChanges.DeadLineCancel = {
        removed: prevDeadline,
        added: nextDeadline,
      };

      // ⚡ Caso especial: solo cambia DeadLineCancel, agregar CanBeCanceled si falta
      if (!deadlineChanges.CanBeCanceled) {
        deadlineChanges.CanBeCanceled = {
          removed: prevCanCancel,
          added: nextCanCancel,
        };
      }
    }

    if (Object.keys(deadlineChanges).length) {
      changes.deadline = deadlineChanges;
    }
  }

  return Object.keys(changes).length ? changes : null;
};

/**
 * 🧼 Normaliza la valuación para evitar nulls estructurales
 */
const normalizeBookingValuation = (valuation, bookingPrice) => {
  if (!valuation) return valuation;

  if (!valuation?.rate?.[0]?.rates?.length) {
    return {
      ...valuation,
      rate: [{ rates: [{ price: bookingPrice }] }],
    };
  }

  return {
    ...valuation,
    rate: [
      {
        ...valuation.rate[0],
        rates: [
          {
            ...valuation.rate[0].rates[0],
            price: valuation.rate[0].rates[0].price ?? bookingPrice,
          },
        ],
      },
    ],
  };
};

const runHotelValuation = async ({
  price,
  id_transaction,
  previousValuation = null,
  service,
}) => {
  try {
    const normalizedPrevious = normalizeBookingValuation(
      previousValuation,
      price
    );

    const rawResult = await service(id_transaction);

    // 🔥 extraemos flag del backend
    const { price_changed = false, ...valuationData } = rawResult || {};

    const normalizedResult = normalizeBookingValuation(valuationData, price);

    let changes = null;

    if (normalizedPrevious) {
      changes = findBookingValuationChanges(
        normalizedPrevious,
        normalizedResult,
        price_changed
      );
    }

    return {
      result: normalizedResult,
      newPrice: normalizedResult?.rate?.[0]?.rates?.[0]?.price ?? null,
      changes,
      priceChanged: price_changed,
      hasError: false,
    };
  } catch (error) {
    console.error('Error running hotel valuation', error);

    return {
      result: null,
      newPrice: null,
      changes: null,
      priceChanged: false,
      hasError: true,
    };
  }
};

export const checkHotelValidation = ({
  price,
  id_transaction,
  previousValuation,
}) =>
  runHotelValuation({
    price,
    id_transaction,
    previousValuation,
    service: getHotelValidate,
  });

export const checkHotelCancellation = ({
  price,
  id_transaction,
  previousValuation,
}) =>
  runHotelValuation({
    price,
    id_transaction,
    previousValuation,
    service: getHotelCancellationFee,
  });
